import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ImageWithFallback } from './ui/image-with-fallback';
import {
    Search,
    MapPin,
    Calendar,
    Users,
    Star,
    Play,
    ArrowRight,
    CheckCircle,
    Zap,
    Heart,
    MessageCircle,
    Camera,
    Globe,
    Link
} from 'lucide-react';

interface LandingPageProps {
    onLogin: () => void;
    onSignUp: () => void;
}

export function LandingPage({ onLogin, onSignUp }: LandingPageProps) {
    const [currentTestimonial, setCurrentTestimonial] = useState(0);

    const testimonials = [
        {
            name: "Sarah Chen",
            role: "Fitness Enthusiast",
            avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=100&h=100&fit=crop",
            content: "I've discovered amazing yoga classes and hiking groups through this platform. The community is incredibly welcoming!"
        },
        {
            name: "Mike Rodriguez",
            role: "Tennis Player",
            avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
            content: "Found my tennis doubles partner here! The activity matching is spot-on and the booking system is seamless."
        },
        {
            name: "Emma Johnson",
            role: "Adventure Seeker",
            avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop",
            content: "From rock climbing to cycling tours, I've tried so many new activities. This platform changed my social life!"
        }
    ];

    const sampleActivities = [
        {
            id: 1,
            title: "Morning Yoga in Central Park",
            organizer: "Zen Studios",
            image: "https://images.unsplash.com/photo-1602827114685-efbb2717da9f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxncm91cCUyMGZpdG5lc3MlMjB5b2dhJTIwY2xhc3N8ZW58MXx8fHwxNzU2MTA2MDIzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
            date: "Tomorrow",
            time: "7:00 AM",
            location: "Central Park",
            participants: 12,
            maxParticipants: 15,
            price: "Free",
            difficulty: "Beginner",
            rating: 4.8,
            tags: ["Yoga", "Outdoor", "Morning"]
        },
        {
            id: 2,
            title: "Tennis Doubles Tournament",
            organizer: "NYC Tennis Club",
            image: "https://images.unsplash.com/photo-1612795023542-7dc469da392a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZW5uaXMlMjBwbGF5ZXJzJTIwb3V0ZG9vcnxlbnwxfHx8fDE3NTYxMDYwMjZ8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
            date: "Sat, Feb 1",
            time: "2:00 PM",
            location: "Tennis Center",
            participants: 8,
            maxParticipants: 16,
            price: "$25",
            difficulty: "Intermediate",
            rating: 4.9,
            tags: ["Tennis", "Tournament", "Competitive"]
        },
        {
            id: 3,
            title: "Rock Climbing Adventure",
            organizer: "Urban Climb",
            image: "https://images.unsplash.com/photo-1626413196980-81aa393bf5cc?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyb2NrJTIwY2xpbWJpbmclMjBncm91cHxlbnwxfHx8fDE3NTYxMDYwMjl8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
            date: "Sun, Feb 2",
            time: "10:00 AM",
            location: "Brooklyn Boulders",
            participants: 6,
            maxParticipants: 10,
            price: "$40",
            difficulty: "Advanced",
            rating: 4.7,
            tags: ["Climbing", "Adventure", "Indoor"]
        }
    ];

    const features = [
        {
            icon: Search,
            title: "Discover Activities",
            description: "Find exciting activities near you with our smart search and filtering system"
        },
        {
            icon: Users,
            title: "Join Communities",
            description: "Connect with like-minded people and build lasting friendships through shared interests"
        },
        {
            icon: Calendar,
            title: "Easy Booking",
            description: "Simple one-click booking with calendar integration and automatic reminders"
        },
        {
            icon: MapPin,
            title: "Location Based",
            description: "Discover activities in your neighborhood or explore new areas nearby"
        },
        {
            icon: Star,
            title: "Quality Assured",
            description: "All organizers are verified and activities are rated by our community"
        },
        {
            icon: Heart,
            title: "Follow Favorites",
            description: "Follow your favorite organizers and never miss their latest activities"
        }
    ];

    const steps = [
        {
            step: 1,
            title: "Create Your Profile",
            description: "Sign up and tell us about your interests and activity preferences",
            icon: "👤"
        },
        {
            step: 2,
            title: "Explore Activities",
            description: "Browse activities by location, type, difficulty, or time that work for you",
            icon: "🔍"
        },
        {
            step: 3,
            title: "Join & Connect",
            description: "Apply to activities, meet new people, and start your adventure",
            icon: "🎉"
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                                <Zap className="w-5 h-5 text-primary-foreground" />
                            </div>
                            <h1 className="text-xl font-bold"><a href="/">PlayHub</a></h1>
                        </div>

                        <div className="flex items-center space-x-4">
                            <Button variant="ghost" onClick={onLogin}>
                                Log In
                            </Button>
                            <Button onClick={onSignUp}>
                                Sign Up
                            </Button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-16 pb-20 bg-gradient-to-br from-primary/5 via-background to-accent/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="space-y-8"
                        >
                            <div className="space-y-4">
                                <motion.h1
                                    className="text-5xl lg:text-6xl font-bold leading-tight"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    Find Your Next
                                    <span className="text-primary block">Adventure</span>
                                </motion.h1>

                                <motion.p
                                    className="text-xl text-muted-foreground max-w-xl"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                >
                                    Discover amazing activities, connect with like-minded people, and build lasting friendships in your community.
                                </motion.p>
                            </div>

                            <motion.div
                                className="flex flex-col sm:flex-row gap-4"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6 }}
                            >
                                <Button size="lg" className="text-lg px-8 py-6" onClick={onSignUp}>
                                    Get Started Free
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                                    <Play className="mr-2 w-5 h-5" />
                                    Watch Demo
                                </Button>
                            </motion.div>

                            <motion.div
                                className="flex items-center space-x-8 text-sm text-muted-foreground"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.8 }}
                            >
                                <div className="flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                    <span>Free to join</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                    <span>Verified organizers</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                    <span>Safe & secure</span>
                                </div>
                            </motion.div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, delay: 0.3 }}
                            className="relative"
                        >
                            <div className="relative">
                                <ImageWithFallback
                                    src="https://images.unsplash.com/photo-1521115507110-6a627e5ce14e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGFjdGl2ZSUyMHBlb3BsZXxlbnwxfHx8fDE3NTYxMDYwMzh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                                    alt="People enjoying activities together"
                                    className="rounded-2xl shadow-2xl w-full h-[600px] object-cover"
                                />

                                {/* Floating stats cards */}
                                <motion.div
                                    className="absolute top-4 right-4 bg-background/90 backdrop-blur-sm rounded-lg p-3 shadow-lg"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 1 }}
                                >
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-primary">10K+</div>
                                        <div className="text-xs text-muted-foreground">Active Users</div>
                                    </div>
                                </motion.div>

                                <motion.div
                                    className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm rounded-lg p-3 shadow-lg"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 1.2 }}
                                >
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-primary">500+</div>
                                        <div className="text-xs text-muted-foreground">Activities Weekly</div>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Sample Activities Section */}
            <section className="py-20 bg-muted/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl font-bold mb-4">Popular Activities</h2>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            See what's happening in your area. From fitness classes to adventure sports.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {sampleActivities.map((activity, index) => (
                            <motion.div
                                key={activity.id}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                whileHover={{ y: -5 }}
                                className="cursor-pointer"
                            >
                                <Card className="overflow-hidden h-full hover:shadow-lg transition-all duration-300">
                                    <div className="relative">
                                        <ImageWithFallback
                                            src={activity.image}
                                            alt={activity.title}
                                            className="w-full h-48 object-cover"
                                        />
                                        <div className="absolute top-3 left-3">
                                            <Badge variant="secondary" className="bg-background/90">
                                                {activity.difficulty}
                                            </Badge>
                                        </div>
                                        <div className="absolute top-3 right-3">
                                            <Badge variant="default" className="bg-primary/90">
                                                {activity.price}
                                            </Badge>
                                        </div>
                                    </div>

                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg line-clamp-2">{activity.title}</CardTitle>
                                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                                            <span>{activity.organizer}</span>
                                            <div className="flex items-center space-x-1">
                                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                                <span>{activity.rating}</span>
                                            </div>
                                        </div>
                                    </CardHeader>

                                    <CardContent className="pt-0 space-y-4">
                                        <div className="space-y-2">
                                            <div className="flex items-center space-x-2 text-sm">
                                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                                <span>{activity.date} at {activity.time}</span>
                                            </div>
                                            <div className="flex items-center space-x-2 text-sm">
                                                <MapPin className="w-4 h-4 text-muted-foreground" />
                                                <span>{activity.location}</span>
                                            </div>
                                            <div className="flex items-center space-x-2 text-sm">
                                                <Users className="w-4 h-4 text-muted-foreground" />
                                                <span>{activity.participants}/{activity.maxParticipants} participants</span>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-1">
                                            {activity.tags.map((tag) => (
                                                <Badge key={tag} variant="outline" className="text-xs">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </div>

                                        <Button className="w-full" onClick={onSignUp}>
                                            Join Activity
                                        </Button>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl font-bold mb-4">Why Choose PlayHub?</h2>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Everything you need to discover, join, and organize amazing activities.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="text-center space-y-4"
                            >
                                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
                                    <feature.icon className="w-8 h-8 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold">{feature.title}</h3>
                                <p className="text-muted-foreground">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-20 bg-muted/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl font-bold mb-4">How It Works</h2>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Get started in just three simple steps.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-3 gap-8">
                        {steps.map((step, index) => (
                            <motion.div
                                key={step.step}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                className="text-center relative"
                            >

                                {index < steps.length - 1 && (
                                    <div className="hidden lg:block absolute top-16 left-full w-full h-px bg-border transform -translate-x-1/2" />
                                )}
                                <div className="relative space-y-6 mt-6">
                                    <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center mx-auto text-3xl">
                                        {step.icon}
                                    </div>

                                    <div className="space-y-3">
                                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center mx-auto text-sm font-bold text-primary">
                                            {step.step}
                                        </div>
                                        <h3 className="text-xl font-bold">{step.title}</h3>
                                        <p className="text-muted-foreground max-w-sm mx-auto">{step.description}</p>
                                    </div>
                                </div>

                            </motion.div>
                        ))}
                    </div>
                </div>
            </section >

            {/* Testimonials Section */}
            < section className="py-20" >
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl font-bold mb-4">What Our Users Say</h2>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Join thousands of happy users who found their community.
                        </p>
                    </motion.div>

                    <div className="relative">
                        <motion.div
                            key={currentTestimonial}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ duration: 0.5 }}
                            className="text-center"
                        >
                            <Card className="max-w-2xl mx-auto">
                                <CardContent className="p-8 space-y-6">
                                    <p className="text-lg italic">"{testimonials[currentTestimonial].content}"</p>
                                    <div className="flex items-center justify-center space-x-4">
                                        <Avatar className="w-12 h-12">
                                            <AvatarImage src={testimonials[currentTestimonial].avatar} />
                                            <AvatarFallback>{testimonials[currentTestimonial].name.slice(0, 2)}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="font-bold">{testimonials[currentTestimonial].name}</p>
                                            <p className="text-sm text-muted-foreground">{testimonials[currentTestimonial].role}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>

                        <div className="flex justify-center space-x-2 mt-8">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    className={`w-3 h-3 rounded-full transition-colors ${index === currentTestimonial ? 'bg-primary' : 'bg-border'
                                        }`}
                                    onClick={() => setCurrentTestimonial(index)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section >

            {/* CTA Section */}
            < section className="py-20 bg-primary text-primary-foreground" >
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="space-y-8"
                    >
                        <h2 className="text-4xl lg:text-5xl font-bold">
                            Ready to Start Your Adventure?
                        </h2>
                        <p className="text-xl opacity-90 max-w-2xl mx-auto">
                            Join thousands of people discovering new activities and building amazing connections every day.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button
                                size="lg"
                                variant="secondary"
                                className="text-lg px-8 py-6"
                                onClick={onSignUp}
                            >
                                Get Started Free
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                className="text-lg px-8 py-6 border-primary-foreground hover:bg-primary-foreground hover:text-primary"
                                onClick={onLogin}
                            >
                                I Have an Account
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </section >

            {/* Footer */}
            < footer className="py-12 bg-background border-t border-border" >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-8">
                        <div className="space-y-4">
                            <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                                    <Zap className="w-5 h-5 text-primary-foreground" />
                                </div>
                                <span className="text-xl font-bold">PlayHub</span>
                            </div>
                            <p className="text-muted-foreground">
                                Connecting people through amazing activities and shared experiences.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-bold mb-4">Platform</h3>
                            <div className="space-y-2 text-muted-foreground">
                                <p>Discover Activities</p>
                                <p>Create Events</p>
                                <p>Join Communities</p>
                                <p>Mobile App</p>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-bold mb-4">Support</h3>
                            <div className="space-y-2 text-muted-foreground">
                                <p>Help Center</p>
                                <p>Safety Guidelines</p>
                                <p>Contact Us</p>
                                <p>Community Rules</p>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-bold mb-4">Company</h3>
                            <div className="space-y-2 text-muted-foreground">
                                <p>About Us</p>
                                <p>Careers</p>
                                <p>Privacy Policy</p>
                                <p>Terms of Service</p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 pt-8 border-t border-border text-center text-muted-foreground">
                        <p>&copy; 2025 PlayHub. All rights reserved.</p>
                    </div>
                </div>
            </footer >
        </div >
    );
}